import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BASE_URL } from '../constants/constants';
import { getValueFromStorage } from '../helpers/helpers';

export const AppointmentsApi = createApi({
  reducerPath: 'appointments',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: headers => {
      headers.set('Content-type', 'application/json');
      headers.set('authorization', `Bearer ${getValueFromStorage('token')}`);
      return headers;
    },
  }),
  endpoints: builder => ({
    getAppointments: builder.query({
      query: ({
        currentPage = 1,
        resultPerPage = 10,
        service,
        status,
        search,
        unpaidOnly,
      }) => ({
        url: `/api/appointment/getAppointments`,
        method: 'GET',
        params: {
          current_page: currentPage,
          result_per_page: resultPerPage,
          service,
          status,
          search,
          unpaid_only: unpaidOnly,
        },
      }),
    }),

    getAppointmentById: builder.query({
      query: id => ({
        url: `/api/appointment/getAppointment/${id}`,
        method: 'GET',
      }),
    }),

    updateAppointment: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/api/appointment/updateAppointment/${id}`,
          method: 'PUT',
          body: body,
        };
      },
      // The Paid checkbox reads straight from the cached list, so without this
      // it stays unticked until the follow-up getAppointments refetch lands.
      // Only payment fields are copied: list rows carry joined and re-priced
      // fields the raw document returned here does not.
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        let response;
        try {
          ({ data: response } = await queryFulfilled);
        } catch {
          return;
        }
        if (response?.error || !response?.data) return;
        const { isPaid, paymentMethod, paidAmount } = response.data;
        const cachedArgs = AppointmentsApi.util.selectCachedArgsForQuery(
          getState(),
          'getAppointments'
        );
        for (const args of cachedArgs) {
          dispatch(
            AppointmentsApi.util.updateQueryData(
              'getAppointments',
              args,
              draft => {
                const row = draft?.data?.appointments?.find(a => a._id === id);
                if (row)
                  Object.assign(row, { isPaid, paymentMethod, paidAmount });
              }
            )
          );
        }
      },
    }),
    updateAppointmentStatus: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/api/appointment/updateAppointmentStatus/${id}`,
          method: 'PUT',
          body: body,
        };
      },
    }),
    checkinAppointment: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/api/appointment/checkinAppointment/${id}`,
          method: 'PUT',
          body: body,
        };
      },
    }),
    checkoutAppointment: builder.mutation({
      query: ({ id, body }) => {
        return {
          url: `/api/appointment/checkOutAppointment/${id}`,
          method: 'PUT',
          body: body,
        };
      },
    }),
    sendInvoice: builder.mutation({
      query: ({ id }) => {
        return {
          url: `/api/appointment/sendInvoice/${id}`,
          method: 'POST',
        };
      },
    }),
    createAppointment: builder.mutation({
      query: body => {
        return {
          url: `/api/appointment/createAppointment`,
          method: 'POST',
          body: body,
        };
      },
    }),
    calculatePrice: builder.mutation({
      query: body => {
        return {
          url: `/api/appointment/calculatePrice`,
          method: 'POST',
          body: body,
        };
      },
    }),
    getAppointmentAuditLog: builder.query({
      query: ({ id, page = 0, limit = 50 }) => ({
        url: `/api/appointment/${id}/auditLog`,
        method: 'GET',
        params: { page, limit },
      }),
    }),
    deleteAppointment: builder.mutation({
      query: id => ({
        url: `/api/appointment/deleteAppointment/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetAppointmentsQuery,
  useLazyGetAppointmentsQuery,
  useGetAppointmentByIdQuery,
  useLazyGetAppointmentByIdQuery,
  useUpdateAppointmentMutation,
  useUpdateAppointmentStatusMutation,
  useCheckinAppointmentMutation,
  useCheckoutAppointmentMutation,
  useSendInvoiceMutation,
  useCreateAppointmentMutation,
  useCalculatePriceMutation,
  useGetAppointmentAuditLogQuery,
  useLazyGetAppointmentAuditLogQuery,
  useDeleteAppointmentMutation,
} = AppointmentsApi;
